import { Link, useLocation } from 'react-router-dom';
import { useTranslation } from '../hooks/useTranslation';
import { useTheme } from '../hooks/useTheme';

// 所有可配置的頁面（用於 user 角色的 visible_pages）
// admin 段順序即為 sidebar 顯示順序：Manage Users → AI Assistant → Container Monitor → Tasks → Error Log
const ALL_PAGES = [
  { path: '/sg-pools', icon: 'bi-table', label: 'SG Pools' },
  { path: '/now-goal', icon: 'bi-table', label: 'Now Goal' },
  { path: '/now-goal-ou', icon: 'bi-graph-up', labelKey: 'nowGoalOU.title' },
  { path: '/method', icon: 'bi-table', label: 'Method' },
  { path: '/users/admin', icon: 'bi-person', labelKey: 'nav.manageUsers', section: 'admin' },
  { path: '/admin/claude', icon: 'bi-robot', labelKey: 'nav.frontendUpdate', section: 'admin' },
  { path: '/admin/container-status', icon: 'bi-hdd-stack', labelKey: 'nav.containerStatus', section: 'admin' },
  { path: '/tasks/long-time', icon: 'bi-list-task', labelKey: 'nav.sgPoolsLongTasks', section: 'admin' },
  { path: '/tasks/short-time', icon: 'bi-list-task', labelKey: 'nav.sgPoolsShortTasks', section: 'admin' },
  { path: '/tasks/now-goal', icon: 'bi-list-task', labelKey: 'nav.nowGoalTasks', section: 'admin' },
  { path: '/errors/sg-pools', icon: 'bi-bug', labelKey: 'nav.sgPoolsErrors', section: 'admin' },
  { path: '/errors/now-goal', icon: 'bi-bug', labelKey: 'nav.nowGoalErrors', section: 'admin' },
];

const Layout = ({ children, userRoles, visiblePages, handleLogout }) => {
  const location = useLocation();
  const { t, language, setLanguage } = useTranslation();
  const { theme, toggleTheme } = useTheme();
  const isActive = (path) => location.pathname === path;

  const isRootOrAdmin = userRoles.includes('root') || userRoles.includes('admin');
  const isUser = userRoles.includes('user');

  // 決定要顯示的頁面
  const getVisiblePages = () => {
    if (isRootOrAdmin) return ALL_PAGES;
    if (isUser) return ALL_PAGES.filter((p) => (visiblePages || []).includes(p.path));
    return []; // readonly 只看到 settings
  };

  const pages = getVisiblePages();
  const mainPages = pages.filter((p) => !p.section);
  const rawAdminPages = pages.filter((p) => p.section === 'admin');

  // 將 task 與 error 細項折成單一連結（路由是合併頁，URL 用 ?source= 區分）。
  // 順序跟著 ALL_PAGES 走，第一次遇到 task/error 時插入折疊項目。
  const TASK_PATHS = new Set(['/tasks/long-time', '/tasks/short-time', '/tasks/now-goal']);
  const ERROR_PATHS = new Set(['/errors/sg-pools', '/errors/now-goal']);
  const adminPages = [];
  let taskInserted = false;
  let errorInserted = false;
  for (const p of rawAdminPages) {
    if (TASK_PATHS.has(p.path)) {
      if (!taskInserted) {
        adminPages.push({ path: '/tasks', icon: 'bi-list-task', labelKey: 'nav.tasks', section: 'admin' });
        taskInserted = true;
      }
    } else if (ERROR_PATHS.has(p.path)) {
      if (!errorInserted) {
        adminPages.push({ path: '/errors', icon: 'bi-bug', labelKey: 'nav.errors', section: 'admin' });
        errorInserted = true;
      }
    } else {
      adminPages.push(p);
    }
  }

  return (
      <div>
          {/* Top Navbar */}
          <nav className="navbar navbar-expand-lg navbar-dark bg-primary">
              <div className="container-fluid">
                  <Link className="navbar-brand" to="/settings">{t('nav.brand')}</Link>
                  <button
                    className="btn btn-sm btn-outline-light ms-2"
                    onClick={() => setLanguage(language === 'zh-TW' ? 'en' : 'zh-TW')}
                  >
                    {language === 'zh-TW' ? 'EN' : '中文'}
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-light ms-2"
                    onClick={toggleTheme}
                    title={theme === 'dark' ? t('nav.switchToLight') : t('nav.switchToDark')}
                  >
                    <i className={`bi ${theme === 'dark' ? 'bi-sun-fill' : 'bi-moon-stars-fill'}`}></i>
                  </button>

                  <button
                      className="navbar-toggler"
                      type="button"
                      data-bs-toggle="collapse"
                      data-bs-target="#sidebarMenu"
                      aria-controls="sidebarMenu"
                      aria-expanded="false"
                      aria-label="Toggle navigation"
                  >
                      <span className="navbar-toggler-icon"></span>
                  </button>
              </div>
          </nav>

          <div className="container-fluid">
              <div className="row">
                  <nav
                      id="sidebarMenu"
                      className="col-md-3 col-lg-2 d-md-block bg-body-tertiary sidebar collapse"
                  >
                      <div className="position-sticky pt-3">
                          <ul className="nav flex-column">
                              <li className="nav-item">
                                  <Link className={`nav-link ${isActive('/settings') ? 'active' : ''}`} to="/settings">
                                      <i className="bi bi-person-gear me-2"></i>{t('nav.userSettings')}
                                  </Link>
                              </li>

                              {mainPages.map((page) => (
                                  <li className="nav-item" key={page.path}>
                                      <Link className={`nav-link ${isActive(page.path) ? 'active' : ''}`} to={page.path}>
                                          <i className={`bi ${page.icon} me-2`}></i>
                                          {page.labelKey ? t(page.labelKey) : page.label}
                                      </Link>
                                  </li>
                              ))}

                              {(adminPages.length > 0 || isRootOrAdmin) && (
                                  <>
                                      <li className="nav-item">
                                          <div className="nav-link disabled">
                                              <i className="bi bi-shield-lock me-2"></i>Admin
                                          </div>
                                      </li>
                                      {adminPages.map((page) => (
                                          <li className="nav-item" key={page.path}>
                                              <Link className={`nav-link ${isActive(page.path) ? 'active' : ''}`} to={page.path}>
                                                  <i className={`bi ${page.icon} me-2`}></i>
                                                  {page.labelKey ? t(page.labelKey) : page.label}
                                              </Link>
                                          </li>
                                      ))}
                                      {isRootOrAdmin && (
                                          <li className="nav-item">
                                              <Link className={`nav-link ${isActive('/docs') ? 'active' : ''}`} to="/docs">
                                                  <i className="bi bi-book me-2"></i>{t('nav.docs')}
                                              </Link>
                                          </li>
                                      )}
                                  </>
                              )}

                              <li className="nav-item">
                                  <button
                                      className="nav-link btn btn-link text-danger"
                                      onClick={handleLogout}
                                  >
                                      <i className="bi bi-box-arrow-right me-2"></i>{t('nav.logout')}
                                  </button>
                              </li>
                          </ul>
                      </div>
                  </nav>

                  <main className="col-md-9 col-lg-10 px-3">
                      {children}
                  </main>
              </div>
          </div>
      </div>
  );
};

// 匯出 ALL_PAGES 供 Users.jsx 使用
export { ALL_PAGES };
export default Layout;
